import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import CourseHeader from "../components/Courses/Shared/CourseHeader";
import ProfessionalCourseApplicationForm from "../components/Courses/ProfessionalCourses/ProfessionalCourseApplicationForm";
import ciscoImg from "../assets/cisco.png";
import cmsApi from "../services/cmsApi";
import { getImageUrl } from "../utils/getImageUrl";
import { getCareerPaths, findCareerPath, findCourse, getCoursesForLevel, capitalize } from "../utils/ciscoCareerPaths";
import CiscoLevelCoursesPage from "./CiscoLevelCoursesPage";

// A single stat block in the sidebar (FORMAT / DURATION / HOURS / FEE).
function StatBlock({ icon, label, value }) {
  return (
    <div className="flex flex-col items-center text-center gap-2">
      <div className="w-10 h-10 rounded-full bg-custom-pink/10 text-custom-pink flex items-center justify-center">
        {icon}
      </div>
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
      <span className="text-xs font-bold text-gray-700">{value || "—"}</span>
    </div>
  );
}

const ICONS = {
  format: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  ),
  calendar: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  clock: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  fee: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V6m0 10v2m0-2c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

// Scrolls to an element, accounting for the sticky site navbar plus any extra
// (e.g. this page's own sticky Overview/Curriculum tab bar).
function scrollToSection(id, extraOffset = 0) {
  const el = document.getElementById(id);
  if (!el) return;
  const navbar = document.querySelector("header.sticky");
  const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 100;
  const top = el.getBoundingClientRect().top + window.pageYOffset - navbarHeight - extraOffset - 16;
  window.scrollTo({ top, behavior: "smooth" });
}

// Measures the live height of the site's sticky navbar so this page's own
// tab bar can pin itself directly beneath it.
function useNavbarHeight() {
  const [height, setHeight] = useState(96);
  useEffect(() => {
    const measure = () => {
      const navbar = document.querySelector("header.sticky");
      if (navbar) setHeight(navbar.getBoundingClientRect().height);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  return height;
}

// Tracks which section (Overview/Curriculum) is currently in view while scrolling,
// so the tab bar highlights the right one even without clicking it.
function useActiveCourseSection(showCurriculum, offset) {
  const [active, setActive] = useState("overview");
  useEffect(() => {
    if (!showCurriculum) {
      setActive("overview");
      return;
    }
    const update = () => {
      const curriculumEl = document.getElementById("course-curriculum");
      setActive(curriculumEl && curriculumEl.getBoundingClientRect().top <= offset + 40 ? "curriculum" : "overview");
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, [showCurriculum, offset]);
  return active;
}

// Modal shown when an achievement badge is clicked.
function AchievementModal({ achievement, onClose }) {
  if (!achievement) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-4 mb-4">
          {achievement.image ? (
            <img
              src={getImageUrl(achievement.image)}
              alt={achievement.title}
              className="w-20 h-20 object-contain flex-shrink-0 rounded-lg"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-20 h-20 rounded-lg bg-custom-pink/10 flex-shrink-0" />
          )}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">{achievement.title}</h3>
            <p className="text-xs text-gray-500 mb-2">
              Issued by <span className="font-semibold text-gray-700">Cisco Networking Academy</span>
            </p>
            <span className="inline-block text-xs font-semibold text-gray-600 border border-gray-300 rounded px-2 py-0.5">
              {achievement.type || "Achievements"}
            </span>
          </div>
        </div>
        {achievement.description && (
          <p className="text-sm text-gray-700 leading-relaxed mb-6">{achievement.description}</p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="w-full bg-custom-pink text-white font-bold py-3 rounded-lg hover:opacity-90 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// Course detail page, e.g.
// /courses/professional-courses/cisco-networking-academy/it-support-specialist/it-customer-support-basics
const CiscoCourseDetailPage = () => {
  const { pathSlug, courseSlug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const navbarHeight = useNavbarHeight();
  const tabBarRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    cmsApi
      .getProgramBySlug("cisco-networking-academy")
      .then((res) => {
        if (res.success && res.program) setProgram(res.program);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [pathSlug, courseSlug]);

  const paths = getCareerPaths(program?.ciscoCertTracksSection ?? null);
  const path = findCareerPath(paths, pathSlug);
  const course = findCourse(path, courseSlug);

  const showCurriculum = course?.showCurriculum === true;
  const showAchievements = course?.showAchievements === true;
  const activeSection = useActiveCourseSection(showCurriculum, navbarHeight);

  // "beginner"/"intermediate"/"advanced" can be a `level` grouping multiple
  // language-specific courses (currently just the Programming path) rather
  // than a single course's own slug — show the filterable list instead.
  if (!loading && path && !course) {
    const levelCourses = getCoursesForLevel(path, courseSlug);
    if (levelCourses.length > 0) {
      return <CiscoLevelCoursesPage pathSlug={pathSlug} levelSlug={courseSlug} />;
    }
  }

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex justify-center items-center py-32">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-custom-pink" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!path || !course) {
    return (
      <div>
        <Navbar />
        <div className="mid-desktop-page container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl py-24 text-center">
          <h1 className="text-3xl font-bold text-custom-pink mb-4">Course not found</h1>
          <p className="text-gray-600 mb-8">
            We couldn&apos;t find that course. It may have been renamed or removed.
          </p>
          <Link
            to="/courses/professional-courses/cisco-networking-academy"
            className="inline-block bg-custom-pink text-white font-bold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
          >
            &larr; Back to Discover Our Certifications
          </Link>
        </div>
        <Footer />
        <FloatingSocialPanels />
      </div>
    );
  }

  const curriculumModules = Array.isArray(course.curriculumModules) ? course.curriculumModules : [];
  const achievements = Array.isArray(course.achievements) ? course.achievements : [];
  const heroImage = course.heroImage || program?.heroImage;

  const goToOverview = () => scrollToSection("course-overview", tabBarRef.current?.offsetHeight || 0);
  // Land a little short of the exact top so the last overview line ("Here's
  // what you will learn.") stays visible above the module list.
  const goToCurriculum = () => scrollToSection("course-curriculum", (tabBarRef.current?.offsetHeight || 0) + 56);

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">
        <CourseHeader
          title={course.name}
          highlight=""
          subtitle={course.description}
          backgroundImage={heroImage ? getImageUrl(heroImage) : ciscoImg}
          cta={{ label: "Start Application", targetId: "application-form" }}
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Professional Courses", href: "/course/professional-courses" },
            { label: "Cisco Networking Academy", href: "/courses/professional-courses/cisco-networking-academy" },
            { label: `${path.name} Career Path`, href: `/courses/professional-courses/cisco-networking-academy/${path.slug}` },
            ...(course.level
              ? [{ label: capitalize(course.level), href: `/courses/professional-courses/cisco-networking-academy/${path.slug}/${course.level}` }]
              : []),
            { label: course.name },
          ]}
        />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl py-16 md:py-24">
          <Link
            to={`/courses/professional-courses/cisco-networking-academy/${path.slug}`}
            className="mb-8 inline-flex items-center gap-2 text-custom-pink font-semibold hover:underline"
          >
            <span aria-hidden="true">&larr;</span> Back to {path.name} Career Path
          </Link>

          <p className="text-sm text-gray-500 mb-8">
            This course is part of the Career Path — <span className="font-semibold text-gray-700">{path.name}</span>
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main column: Overview, then Curriculum right below it (no separate section to click into) */}
            <div className="lg:col-span-2">
              {showCurriculum && (
                <div
                  ref={tabBarRef}
                  className="sticky z-20 bg-white flex gap-1 border-b border-gray-200 mb-8"
                  style={{ top: navbarHeight }}
                >
                  <button
                    type="button"
                    onClick={goToOverview}
                    className={`px-5 py-3 text-sm md:text-base font-semibold border-b-2 -mb-px transition-colors ${
                      activeSection === "overview"
                        ? "text-custom-pink border-custom-pink"
                        : "text-gray-500 border-transparent hover:text-gray-800"
                    }`}
                  >
                    Overview
                  </button>
                  <button
                    type="button"
                    onClick={goToCurriculum}
                    className={`px-5 py-3 text-sm md:text-base font-semibold border-b-2 -mb-px transition-colors ${
                      activeSection === "curriculum"
                        ? "text-custom-pink border-custom-pink"
                        : "text-gray-500 border-transparent hover:text-gray-800"
                    }`}
                  >
                    Curriculum
                  </button>
                </div>
              )}

              <div id="course-overview">
                {course.overviewContent ? (
                  course.overviewContent.split(/\n\n+/).filter(Boolean).map((p, i) => (
                    <p key={i} className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 whitespace-pre-wrap">{p}</p>
                  ))
                ) : (
                  <p className="text-gray-500 italic">Overview content coming soon.</p>
                )}
              </div>

              {showCurriculum && (
                <div id="course-curriculum" className="mt-4">
                  {curriculumModules.length > 0 ? (
                    <ul className="space-y-3">
                      {curriculumModules.map((mod, i) => (
                        <li key={i} className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-4">
                          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-custom-pink/10 text-custom-pink flex items-center justify-center text-sm font-bold">
                            {i + 1}
                          </span>
                          <span className="text-gray-800 font-medium">{mod}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 italic">Curriculum content coming soon.</p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar: stats + achievements + skills */}
            <div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
                <div className="grid grid-cols-2 gap-6">
                  <StatBlock icon={ICONS.format} label="Format" value={course.format} />
                  <StatBlock icon={ICONS.calendar} label="Duration" value={course.duration} />
                  <StatBlock icon={ICONS.clock} label="Hours" value={course.hours} />
                  <StatBlock icon={ICONS.fee} label="Fee" value={course.fee} />
                </div>
              </div>

              {showAchievements && (
                <div className="bg-[#F8F8F8] border border-gray-200 rounded-2xl p-6 mb-6">
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">Achievements</h4>
                  {achievements.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-3">
                      {achievements.map((a, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSelectedAchievement(a)}
                          title={a.title}
                          aria-label={a.title}
                          className="w-16 h-16 rounded-lg border-2 border-transparent hover:border-custom-pink transition-colors flex-shrink-0 bg-custom-pink/10 flex items-center justify-center"
                        >
                          {a.image ? (
                            <img
                              src={getImageUrl(a.image)}
                              alt={a.title}
                              className="w-full h-full object-contain"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <span className="font-bold text-custom-pink text-sm">
                              {a.title?.charAt(0) || "?"}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Badges coming soon.</p>
                  )}
                </div>
              )}

              {course.skills && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
                  <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Skills You Will Learn</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">{course.skills}</p>
                </div>
              )}

              <div className="rounded-2xl p-6 text-white bg-gradient-to-br from-custom-pink to-gold shadow-lg">
                <h5 className="text-lg font-bold mb-2">Ready to get started?</h5>
                <p className="text-sm text-white/90 mb-5 leading-relaxed">
                  Apply for {course.name} through JRIIT&apos;s Cisco Networking Academy partnership.
                </p>
                <a
                  href="#application-form"
                  className="block w-full text-center bg-white text-custom-pink font-bold py-3 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Apply Now
                </a>
              </div>
            </div>
          </div>

          <div id="application-form" className="mt-16">
            <ProfessionalCourseApplicationForm
              programName={`Cisco Networking Academy — ${course.name}`}
              fieldLabel="Professional Course"
            />
          </div>
        </div>
      </div>
      <Footer />
      <FloatingSocialPanels />

      <AchievementModal achievement={selectedAchievement} onClose={() => setSelectedAchievement(null)} />
    </div>
  );
};

export default CiscoCourseDetailPage;
